import { Injectable } from '@angular/core';
import { AbstractControl, AsyncValidatorFn } from '@angular/forms';
import { Observable, of, timer } from 'rxjs';
import { debounceTime, distinctUntilChanged, first, map, switchMap } from 'rxjs/operators';
import { ClientsService } from '../services/clients.service';
import { ClassifierService, PerformersService, UsersService } from '../services';
import { PersonCodeType } from './person-code-type.enum';
import { InvoicesService } from '../services/invoices.service';
import { SinvoicesService } from '../services/sinvoices.service';
import { AbsencesService } from '../services/absences.service';
import moment from 'moment';

@Injectable({ providedIn: 'root' })
export class AsyncValidatorService {
    constructor(
        private clientsService: ClientsService,
        private usersService: UsersService,
        private performersService: PerformersService,
        private classifiersService: ClassifierService,
        private invoicesService: InvoicesService,
        private sinvoicesService: SinvoicesService,
        private absencesService: AbsencesService,
    ) {}

    personCodeValidator(initialValue: string, id: number, type: PersonCodeType = PersonCodeType.Client): AsyncValidatorFn {
        return (control: AbstractControl): Observable<{ [key: string]: any } | null> => {
            if (!control.value && !initialValue) {
                return of(null);
            }

            if (control.value === initialValue) {
                return of(null);
            }
            return control.valueChanges.pipe(
                debounceTime(700),
                distinctUntilChanged(),
                switchMap((val) => {
                    switch (type) {
                        case PersonCodeType.Client:
                            return this.clientsService.isPersonCodeUnique(val, id);
                        case PersonCodeType.User:
                            return this.usersService.isPersonCodeUnique(val, id);
                        case PersonCodeType.Performer:
                            return this.performersService.isPersonCodeUnique(val, id);
                    }
                }),
                map((isUnique) => (isUnique ? null : { error: true, duplicated: true })),
                first(),
            );
        };
    }

    usernameValidator(initialValue: string, id: number): AsyncValidatorFn {
        return (control: AbstractControl): Observable<{ [key: string]: any } | null> => {
            if (!control.value && !initialValue) {
                return of(null);
            }

            if (control.value === initialValue) {
                return of(null);
            }

            return control.valueChanges.pipe(
                debounceTime(700),
                distinctUntilChanged(),
                switchMap((val) => {
                    return this.usersService.isUsernameUnique(val, id);
                }),
                map((isUnique) => (isUnique ? null : { error: true, duplicated: true })),
                first(),
            );
        };
    }

    classifiersOriginalLuidaisIdValidator(initialValue: number, parentId: number): AsyncValidatorFn {
        return (control: AbstractControl): Observable<{ [key: string]: any } | null> => {
            if (!control.value && !initialValue) {
                return of(null);
            }

            if (control.value === initialValue) {
                return of(null);
            }
            return control.valueChanges.pipe(
                debounceTime(700),
                distinctUntilChanged(),
                switchMap((val) => {
                    return this.classifiersService.isOriginalLuidaisIdUnique(parentId, val);
                }),
                map((isUnique) => (isUnique ? null : { error: true, duplicated: true })),
                first(),
            );
        };
    }

    invoiceNumberValidator(initialValue: string, id: number): AsyncValidatorFn {
        return (control: AbstractControl): Observable<{ [key: string]: any } | null> => {
            if (!control.value && !initialValue) {
                return of(null);
            }

            if (control.value === initialValue) {
                return of(null);
            }

            return control.valueChanges.pipe(
                debounceTime(700),
                distinctUntilChanged(),
                switchMap((val: string): Observable<boolean> => {
                    return this.invoicesService.isInvoiceNumberUnique(val, id);
                }),
                map((isUnique: boolean) => (isUnique ? null : { error: true, duplicated: true })),
                first(),
            );
        };
    }

    sinvoiceNumberValidator(initialValue: string, id: number): AsyncValidatorFn {
        return (control: AbstractControl): Observable<{ [key: string]: any } | null> => {
            if (!control.value && !initialValue) {
                return of(null);
            }

            if (control.value === initialValue) {
                return of(null);
            }

            return control.valueChanges.pipe(
                debounceTime(700),
                distinctUntilChanged(),
                switchMap((val: string): Observable<boolean> => {
                    return this.sinvoicesService.isSinvoiceNumberUnique(val, id);
                }),
                map((isUnique: boolean) => (isUnique ? null : { error: true, duplicated: true })),
                first(),
            );
        };
    }

    // Checks that the absence's start/end dates don't overlap with another (non-rejected) absence of the same performer
    absenceDateRangeOverlapValidator(performerId: number, absenceId: number, initialStartDate: string, initialEndDate: string): AsyncValidatorFn {
        return (control: AbstractControl): Observable<{ [key: string]: any } | null> => {
            const startDate = control.parent?.get('startDate')?.value;
            const endDate = control.value;

            if (!startDate || !endDate) {
                return of(null);
            }

            if (moment(startDate).isSame(initialStartDate, 'day') && moment(endDate).isSame(initialEndDate, 'day')) {
                return of(null);
            }

            return timer(700).pipe(
                switchMap(
                    (): Observable<boolean> =>
                        this.absencesService.isDateRangeOverlapping(
                            performerId,
                            moment(startDate).format('YYYY-MM-DD'),
                            moment(endDate).format('YYYY-MM-DD'),
                            absenceId,
                        ),
                ),
                map((isOverlapping: boolean) => (isOverlapping ? { absenceDatesOverlapping: true } : null)),
                first(),
            );
        };
    }
}
